import React, { useState, useEffect, useRef } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import * as yup from 'yup';

import SubmitButton from './global/SubmitButton';
import Input from './global/Input';
import Breadcrumb from './global/Breadcrumb';
import GridContainer from './global/GridContainer';
import RadioButtonGroup from './global/RadioButtonGroup';
import RadioButton from './global/RadioButton';
import Select from './global/Select';
import Fieldset from './global/Fieldset';

import H1 from './../typography/H1';
import Legend from './../typography/Legend';
import Anchor from './../typography/Anchor';

import Response from './Response';
import TransactionField from './global/TransactionField';

import BaseTransition from '../transitions/BaseTransition';

import useElementSize from '../hooks/useElementSize';
import useYupValidationResolver from '../hooks/useYupValidationResolver';

import { trackPromise } from '../promise-tracker';

import Captcha from '../captcha/CaptchaWrapper';
import { SITE_KEY } from '../const/Const';
import { TAX } from '../const/Const';

import { stripDollarSign } from '../../utils';

const oneTimeTopUpValidationSchema = yup.object().shape({
  paymentMethod: yup.string(),
  ordTopUpAmount: yup
    .string()
    .required()
    .test('invalid-amount', 'Amount must have no more than two decimal digits and must be between $1.00 and $999.99 ', amount => {
      const transformedAmount = stripDollarSign(amount);
      return /^\d+(\.\d{1,2})?$/.test(transformedAmount) && Number.parseFloat(transformedAmount) >= 1 && Number.parseFloat(transformedAmount) < 1000;
    }),
  ordPhoneNumber: yup
    .string()
    .required()
    .matches(/^(\([0-9]|[0-9])(\d{2}|\d{2}\))(-|\.|\s)?\d{3}(-|\.|\s)?\d{4}$/)
    .when('$state', (state, schema) =>
      state !== undefined
        ? schema.test('custom-is-prepaid', 'Not a valid prepaid number', number => {
            const transformedNumber = number.replace(/[^0-9]/g, '');
            if (transformedNumber.length === 0) {
              return new Promise(resolve => resolve(false));
            } else if (state.prepaidNumberValidationStatus[transformedNumber] !== undefined) {
              // To prevent unnecessary BE calls
              return new Promise(resolve => resolve(state.prepaidNumberValidationStatus[transformedNumber]));
            } else {
              return fetch(`/api/phonenumbervalidator?mdn=${transformedNumber}`)
                .then(async res => {
                  const { data } = await res.json();
                  state.setPrepaidNumberValidationStatus({ [transformedNumber]: data.validNumber === 'true' });
                  return data.validNumber === 'true';
                })
                .catch(err => {
                  console.error(err);
                  return false; // prevent from submitting the form
                });
            }
          })
        : schema
    ),
  ordFirstName: yup.string().required(),
  ordLastName: yup.string().required(),
  ordEmailAddress: yup
    .string()
    .email()
    .required(),
  ordAddress1: yup.string().required(),
  ordCity: yup.string().required(),
  ordPostalCode: yup
    .string()
    .required()
    .matches(/[a-zA-Z][0-9][a-zA-Z](-| |)[0-9][a-zA-Z][0-9]/),
  trnCardNumber: yup
    .string()
    .required()
    .matches(
      /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
    ),
  trnExpirationDate: yup
    .string()
    .required()
    .matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/),
  trnCardCvd: yup
    .string()
    .required()
    .matches(/^([0-9]){3,4}$/),
  // trnCardNumber: yup.string().when('paymentMethod', (paymentMethod, schema) => {
  //   return paymentMethod === 'cc'
  //     ? schema
  //         .required()
  //         .matches(
  //           /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
  //         )
  //     : schema;
  // }),
  // trnExpirationDate: yup.string().when('paymentMethod', (paymentMethod, schema) => {
  //   return paymentMethod === 'cc' ? schema.required().matches(/^(0[1-9]|1[0-2])\/?([0-9]{2})$/) : schema;
  // }),
  // trnCardCvd: yup.string().when('paymentMethod', (paymentMethod, schema) => {
  //   return paymentMethod === 'cc' ? schema.required().matches(/^([0-9]){3,4}$/) : schema;
  // }),
});

function PrepaidOneTimeTopup() {
  let search = null;
  let transDate = new Date();

  const [isCreditCard, setIsCreditCard] = useState(true);
  const [hasTopupAmount, setHasTopupAmount] = useState(false);
  const [topUpAmount, setTopupAmount] = useState(null);
  const [APICall, setAPICall] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState({});
  const [interacResponse, setInteracResponse] = useState({});
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [isFormValidating, setIsFormValidating] = useState(false);
  const [prepaidNumberValidationStatus, setPrepaidNumberValidationStatus] = useState({});
  const [tooltips, setTooltips] = useState({});

  if (typeof window !== 'undefined') {
    search = window.location.search;
  }
  const params = new URLSearchParams(search);
  const ref2 = params.get('ref2');
  const number = params.get('ordPhoneNumber');
  useEffect(() => {
    if (ref2) {
      setTopupAmount(ref2.slice(5));
      setHasTopupAmount(true);
    }
    if (number) {
      setPhoneNumber(number);
    }
  });

  useEffect(() => {
    // just a workaround to ensure that boundary element in the tooltip popperOptions is already in the DOM
    // TO-DO: find a better solution
    setTooltips({
      topupAmount: 'Enter the auto top-up amount (between $1.00 and $999.99) to apply.',
      firstName: 'Must be entered exactly as it appears on your credit card.',
      email: 'For purchase confirmation. Will not be shared with any other company.',
      cvv: 'Last 3 digits on the back of your credit card.',
    });
  }, []);

  useEffect(() => {
    const existingReturnUrl = sessionStorage.getItem('returnUrl');

    console.log('Existing returnUrl:', existingReturnUrl);
    console.log('document.referrer:', document.referrer);

    // Only set it once (so redirects don’t overwrite it)
    if (!existingReturnUrl && document.referrer) {
      sessionStorage.setItem('returnUrl', document.referrer);
      console.log('Saved returnUrl:', document.referrer);
    }
  }, []);

  console.log(paymentResponse);

  const resolver = useYupValidationResolver(oneTimeTopUpValidationSchema, setIsFormValidating);

  let methods = useForm({
    validationResolver: resolver,
    validationContext: {
      state: {
        prepaidNumberValidationStatus,
        setPrepaidNumberValidationStatus,
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    submitFocusError: true,
    defaultValues: {
      ordTopUpAmount: '$',
    },
  });
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);
  useEffect(() => {
    register({ name: 'captchaToken' }, { required: true });
  });
  const { errors, setValue, register, triggerValidation } = { ...methods };

  const [ref, sizes] = useElementSize(true);

  console.log(errors);

  const handleClick = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      recaptchaRef.current.execute();
    } else {
      // still try to submit the form since RHF won't allow that but nevertheless will focus into the first invalid field
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  const onSubmit = data => {
    console.log(data);

    // if (data.paymentMethod === 'cc') {
    const token = {
      number: data.trnCardNumber,
      expiry_month: data.trnExpirationDate.slice(0, 2),
      expiry_year: data.trnExpirationDate.slice(-2),
      cvd: data.trnCardCvd,
    };

    trackPromise(
      fetch('https://api.na.bambora.com/scripts/tokenization/tokens', {
        method: 'post',
        headers: {
          'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: JSON.stringify(token),
      })
    )
      .then(response => response.json())
      .then(response => {
        const transformedOrdTopUpAmount = stripDollarSign(data.ordTopUpAmount);
        const ordAmount = topUpAmount ? Number.parseFloat(topUpAmount).toFixed(2) : Number.parseFloat(transformedOrdTopUpAmount).toFixed(2);
        const dateStamp = new Date();

        console.log('Tokenize request succeeded with JSON response', response);

        trackPromise(
          fetch('/api/payment/prepaid', {
            // fetch(`http://localhost:9080/api/payment/prepaid`, {
            // fetch('https://api.na.bambora.com/v1/payments', {
            method: 'post',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              order_number: `${data.ordPhoneNumber.replace(/[^0-9]/g, '')}${dateStamp.getMonth() +
                1}${dateStamp.getDay()}${dateStamp.getFullYear()}${dateStamp.getHours()}${dateStamp.getMinutes()}${dateStamp.getSeconds()}`,
              amount: ordAmount * TAX,
              // payment_method: data.paymentMethod === 'cc' ? 'token' : 'interac',
              payment_method: 'token',
              billing: {
                name: `${data.ordFirstName} ${data.ordLastName}`,
                address_line1: data.ordAddress1,
                address_line2: data.ordAddress2,
                city: data.ordCity,
                province: data.ordProvince,
                country: 'CA',
                postal_code: data.ordPostalCode,
                phone_number: data.ordPhoneNumber.replace(/[^0-9]/g, ''),
                email_address: data.ordEmailAddress,
              },
              custom: {
                ref1: data.ordPhoneNumber.replace(/[^0-9]/g, ''),
                ref2: `Basic${ordAmount}`,
                ref3: 'SMS,Email',
                ref4: `PST=${ordAmount * 6},GST=${ordAmount * 5}`,
              },
              token: {
                complete: true,
                code: response.token,
                name: `${data.ordFirstName} ${data.ordLastName}`,
              },
              captchaToken: data.captchaToken,
            }),
          })
        )
          .then(response => response.json())
          .then(response => {
            setPaymentResponse(response.data);
            setAPICall(true);
            console.log(paymentResponse);
          })
          .catch(error => {
            console.log('Request failed', error);
          });
      })
      .catch(error => {
        console.log('Request failed', error);
      });
    // } else {
    //   const ordAmount = topUpAmount ? Number.parseFloat(topUpAmount).toFixed(2) : Number.parseFloat(data.ordTopUpAmount).toFixed(2);
    //   const dateStamp = new Date();

    //   trackPromise(
    //     fetch('/api/payment/prepaid', {
    //       // fetch('http://localhost:9080/api/payment/prepaid', {
    //       // fetch('https://api.na.bambora.com/v1/payments', {
    //       method: 'post',
    //       headers: {
    //         'Content-Type': 'application/json',
    //         // Authorization: 'Passcode ' + btoa('186870000:Ee3D65926ad34730b8ec7293411bE539'),
    //       },
    //       body: JSON.stringify({
    //         order_number: `${data.ordPhoneNumber.replace(/[^0-9]/g, '')}${dateStamp.getMonth() +
    //           1}${dateStamp.getDay()}${dateStamp.getFullYear()}${dateStamp.getHours()}${dateStamp.getMinutes()}${dateStamp.getSeconds()}`,
    //         amount: ordAmount * TAX,
    //         payment_method: data.paymentMethod === 'cc' ? 'token' : 'interac',
    //         billing: {
    //           name: `${data.ordFirstName} ${data.ordLastName}`,
    //           address_line1: data.ordAddress1,
    //           address_line2: data.ordAddress2,
    //           city: data.ordCity,
    //           province: data.ordProvince,
    //           country: 'CA',
    //           postal_code: data.ordPostalCode,
    //           phone_number: data.ordPhoneNumber.replace(/[^0-9]/g, ''),
    //           email_address: data.ordEmailAddress,
    //         },
    //         custom: {
    //           ref1: data.ordPhoneNumber.replace(/[^0-9]/g, ''),
    //         },
    //         captchaToken: data.captchaToken,
    //       }),
    //     })
    //   )
    //     .then(response => response.json())
    //     .then(response => {
    //       setInteracResponse(response.data.contents);
    //       sessionStorage.setItem('m_data', response.data.merchant_data);
    //       // setInteracResponse(response.contents);
    //       // sessionStorage.setItem('m_data', response.merchant_data);
    //       sessionStorage.setItem('profile', 'prepaid');
    //       document.querySelector('#frmIOnline').submit();
    //     })
    //     .catch(error => {
    //       console.log('Request failed', error);
    //     });
    // }
  };

  const onChangePaymentMethod = () => {
    setIsCreditCard(!isCreditCard);
  };

  const onCapthcaChange = token => {
    setValue('captchaToken', token);
    if (token) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  };

  return (
    <main className="flex-auto">
      <Helmet
        htmlAttributes={{
          lang: 'en',
        }}
      >
        <title>SaskTel.com | noSTRINGS Prepaid Wireless One Time Top‑up Form</title>
      </Helmet>
      {Object.keys(paymentResponse).length === 0 && paymentResponse.constructor === Object && (
        <div className="flex flex-col font-museo font-w-300">
          <div className="flex-shrink px-15 md:px-30 lg:px-45 mx-auto my-5 md:my-0 max-w-w-7xl w-full">
            <Breadcrumb
              breadcrumbs={[
                {
                  label: 'Home',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
                },
                {
                  label: 'Wireless',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
                },
                {
                  label: 'noStrings Prepaid',
                  link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
                },
              ]}
            />
            <H1>noSTRINGS Prepaid Wireless One Time Top‑up</H1>
          </div>
          <div className="flex-auto border-t-1 border-light-grey">
            <section className="pt-30 pb-50 px-15 md:py-50 md:px-30 lg:px-0 max-w-w-3xl mx-auto">
              <FormContext {...methods} isFormValidating={isFormValidating}>
                <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef} classes="flex flex-col" id="form">
                  <Fieldset custom classes="flex flex-col">
                    <div className="px-5 md:px-0 pb-30 md:pb-50 w-auto large-12 columns">
                      <div className="font-museo font-w-300 leading-22 text-17">Use your credit card to instantly top-up your account.</div>
                    </div>
                  </Fieldset>
                  <Fieldset>
                    <Legend>Top-up Information</Legend>
                    <div className="w-auto m-0 p-0">
                      {phoneNumber ? (
                        <div className="pl-5 pr-5 md:pl-0 md:pr-15 pb-30 w-full md:w-1\/2 m-0">
                          <label htmlFor="ordPhoneNumber" className="text-12 leading-12 tracking-1 font-mont-700 font-w-400 uppercase">
                            Wireless phone number
                          </label>
                          <p className="text-17 leading-22 font-w-400 tealeaf-mask">{phoneNumber}</p>
                          <input type="hidden" name="ordPhoneNumber" id="ordPhoneNumber" value={phoneNumber} ref={register} />
                        </div>
                      ) : (
                        <GridContainer col="6" gap="30" mdcol="12" mdgap="30">
                          <Input
                            type="tel"
                            placeholder="555-555-5555"
                            spacingClasses="col-span-6 pb-30"
                            label="Wireless phone number"
                            for="ordPhoneNumber"
                            error={errors.ordPhoneNumber}
                          />
                        </GridContainer>
                      )}
                    </div>
                    <div className="w-auto m-0 p-0">
                      {topUpAmount ? (
                        <div className="pl-5 pr-5 md:pl-0 md:pr-15 pb-30 w-full md:w-1\/2 m-0">
                          <label htmlFor="trnAmountPreTax" className="text-12 leading-12 tracking-1 font-mont-700 font-w-400 uppercase">
                            Top-up amount
                          </label>
                          <p>${topUpAmount}.00</p>
                          <input type="hidden" name="ordTopUpAmount" id="ordTopUpAmount" value={topUpAmount} ref={register} />
                        </div>
                      ) : (
                        <div className="pb-30">
                          <GridContainer col="6" gap="30" mdcol="12" mdgap="30">
                            <Input
                              placeholder="Enter Amount"
                              prefix="$"
                              spacingClasses="col-span-6 pb-30"
                              label="Top-up amount"
                              for="ordTopUpAmount"
                              tooltip={tooltips.topupAmount}
                              tooltipName="topup-tooltip"
                              error={errors.ordTopUpAmount}
                              inputMode="numeric"
                            />
                          </GridContainer>
                        </div>
                      )}
                    </div>
                  </Fieldset>

                  <Fieldset classes="pt-30 pb-0 md:pt-40 md:pb-10">
                    <Legend>Billing Address Information</Legend>
                    <GridContainer col="6" gap="0" mdcol="12" mdgap="30">
                      <Input
                        spacingClasses="col-span-6 pb-30"
                        label="First name"
                        for="ordFirstName"
                        tooltip={tooltips.firstName}
                        tooltipName="first-name-tooltip"
                        error={errors.ordFirstName}
                      />
                      <Input spacingClasses="col-span-6 pb-30" label="Last name" for="ordLastName" error={errors.ordLastName} />
                      <Input
                        spacingClasses="col-span-6 pb-30"
                        label="Email"
                        for="ordEmailAddress"
                        tooltip={tooltips.email}
                        tooltipName="email-tooltip"
                        error={errors.ordEmailAddress}
                      />
                      <Input spacingClasses="col-start-1 col-end-7 pb-30" label="Address line 1" for="ordAddress1" error={errors.ordAddress1} />
                      <Input optional spacingClasses="col-span-6 pb-30" label="Address line 2" for="ordAddress2" />
                      <Input spacingClasses="col-span-6 pb-30" label="City / Town" for="ordCity" error={errors.ordCity} />
                      <Select
                        spacingClasses="col-span-6 md:col-span-3"
                        label="Province"
                        name="ordProvince"
                        for="ordProvince"
                        id="ordProvince"
                        options={['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']}
                        defaultValue="SK"
                      />
                      <Input
                        placeholder="A5A 5A5"
                        mask="a9a 9a9"
                        spacingClasses="col-span-6 md:col-span-3 pb-30"
                        label="Postal code"
                        for="ordPostalCode"
                        error={errors.ordPostalCode}
                      />
                    </GridContainer>
                  </Fieldset>
                  <Fieldset classes="py-30 md:py-40">
                    <Legend>Payment Information</Legend>
                    {/* <RadioButtonGroup name="paymentMethod" label="Payment Method" for="paymentMethod" classes="flex flex-col">
                      <RadioButton name="paymentMethod" label="Credit card" value="cc" isSelected={isCreditCard} click={onChangePaymentMethod} />
                      <RadioButton name="paymentMethod" label="Interac Online" value="IO" isSelected={!isCreditCard} click={onChangePaymentMethod} />
                    </RadioButtonGroup> */}

                    {/* {isCreditCard && (
                      <GridContainer col="6" gap="0" mdcol="12" mdgap="30">
                        <Input placeholder="1234 5678 9087 6543" spacingClasses="col-span-6 pb-30" label="Card number" for="trnCardNumber" error={errors.trnCardNumber} />
                        <Input placeholder="MM/YY" spacingClasses="col-span-6 md:col-span-3 pb-30" label="Expiration date" for="trnExpirationDate" error={errors.trnExpirationDate} />
                        <Input spacingClasses="col-span-6 md:col-span-3 pb-30" label="CVV" for="trnCardCvd" tooltip error={errors.trnCardCvd} />
                      </GridContainer>
                    )} */}

                    <div ref={ref}>
                      <BaseTransition in={isCreditCard} sizes={sizes ? sizes.height : 0}>
                        <GridContainer col="6" gap="0" mdcol="12" mdgap="30">
                          <Input
                            placeholder="1234 5678 9087 6543"
                            spacingClasses="col-span-6 pb-30"
                            label="Card number"
                            for="trnCardNumber"
                            error={errors.trnCardNumber}
                          />
                          <Input
                            placeholder="MM/YY"
                            mask="99\/99"
                            spacingClasses="col-span-6 md:col-span-3 pb-30"
                            label="Expiration date"
                            for="trnExpirationDate"
                            error={errors.trnExpirationDate}
                          />
                          <Input
                            spacingClasses="col-span-6 md:col-span-3 pb-30"
                            label="CVV"
                            length="4"
                            for="trnCardCvd"
                            tooltip={tooltips.cvv}
                            tooltipName="cvv-tooltip"
                            error={errors.trnCardCvd}
                          />
                        </GridContainer>
                      </BaseTransition>
                    </div>

                    <div className="row mt-5 md:mt-0">
                      <div className="px-5 md:px-0 pb-30 w-auto large-12 columns">
                        {/* <div className="font-museo-300 font-w-400 leading-18 text-14">
                          This service is for existing SaskTel wireless customers only. You must be a registered online banking customer with a participating
                          institution to use the INTERAC® Online service. Learn more about{' '}
                          <Anchor href="https://www.interac.ca" aria="This link will take you to www.interac.ca">
                            INTERAC® Online service.
                          </Anchor>
                        </div> */}
                        <div className="font-museo font-w-300 leading-18 text-14">This service is for existing SaskTel wireless customers only.</div>
                      </div>
                    </div>
                    <Captcha theme="light" ref={recaptchaRef} sitekey={SITE_KEY} size={'invisible'} badge={'inline'} onChange={onCapthcaChange} />
                  </Fieldset>
                  <Fieldset custom classes="pt-30 md:pt-40 flex flex-col">
                    <div className="w-auto m-0 p-0 flex flex-row">
                      <SubmitButton value="Submit" type="button" name="submit" buttonType="primary" classes="m-auto" click={handleClick}></SubmitButton>
                    </div>
                  </Fieldset>
                </form>
              </FormContext>
            </section>
          </div>
        </div>
      )}
      <div className="flex flex-col font-museo-300" role="status">
        {paymentResponse.approved && paymentResponse.approved === '1' && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={true}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Transaction Approved"
            buttonValue="back"
            buttonOnClick={() => {
              console.log('Referrer:', document.referrer);
              const returnUrl = sessionStorage.getItem('returnUrl');

              if (returnUrl) {
                window.location.href = returnUrl;
              } else {
                window.history.back(); // fallback
              }
            }}
          >
            <div className="font-museo font-w-300 leading-22 text-17 mb-50">
              Thanks for your order! You'll receive a confirmation text and email when your account is topped up.
            </div>
            <TransactionField label="Status" content="Approved" />
            <TransactionField label="Transaction ID" content={paymentResponse.order_number} sensitive />
            <TransactionField label="Authorization code" content={paymentResponse.auth_code} />
            <TransactionField label="Billed amount" content={`\$${paymentResponse.amount.toFixed(2)} (including taxes)`} />
            <TransactionField label="Applied to SaskTel wireless phone number" content={paymentResponse.custom.ref1} sensitive />
            <TransactionField label="Transaction date" content={`${transDate.toLocaleDateString()} ${transDate.toLocaleTimeString()}`} />
          </Response>
        )}
        {paymentResponse.message !== 'ERROR' && !paymentResponse.approved && APICall && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={false}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Transaction Declined"
            buttonValue="back"
            buttonOnClick={() => {
              console.log('Referrer:', document.referrer);
              const returnUrl = sessionStorage.getItem('returnUrl');

              if (returnUrl) {
                window.location.href = returnUrl;
              } else {
                window.history.back(); // fallback
              }
            }}
          >
            <div className="w-auto m-0 p-0 mb-50 text-center">
              <p className="mb-15 md:mb-20">Your transaction has been declined.</p>
              <p className="mb-0">
                Please make sure you've entered your card details and personal information correctly. If the problem persists, we suggest trying another credit
                card or contacting your financial institution.
              </p>
            </div>
            {/* <TransactionField label="Transaction ID" content="10103342" />
            <TransactionField label="Status" content="Invalid Card Number" /> */}
            <TransactionField label="Transaction date" content={`${transDate.toLocaleDateString()} ${transDate.toLocaleTimeString()}`} />
          </Response>
        )}
        {paymentResponse.message === 'ERROR' && (
          <Response
            name="paymentMethod"
            label="Payment Method"
            isApproved={false}
            breadCrumb={[
              {
                label: 'Home',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/',
              },
              {
                label: 'Wireless',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/',
              },
              {
                label: 'noStrings Prepaid',
                link: 'https://www.sasktel.com/wps/wcm/connect/content/home/wireless/prepaid/',
              },
            ]}
            header="Submission Error"
            buttonValue="back"
            buttonOnClick={() => {
              console.log('Referrer:', document.referrer);
              const returnUrl = sessionStorage.getItem('returnUrl');

              if (returnUrl) {
                window.location.href = returnUrl;
              } else {
                window.history.back(); // fallback
              }
            }}
          >
            <div className="flex flex-col items-center mb-15 md:mb-20 text-17 leading-22">
              <p className="mb-0 text-center">Your submission could not be processed due to the following error(s):</p>
              <ul className="mb-0 mt-10 list-disc list-inside mx-auto flex flex-col items-start">
                <li className="mt-10">Invalid billing period</li>
                <li className="mt-10">Invalid billing interval</li>
                <li className="mt-10">Invalid first billing date</li>
              </ul>
            </div>
          </Response>
        )}
      </div>
      {interacResponse && (
        <div
          className="hidden"
          dangerouslySetInnerHTML={{
            __html: unescape(interacResponse).replace(/\+/gm, ' '),
          }}
        />
      )}
    </main>
  );
}

export default PrepaidOneTimeTopup;
